import React, { useState, useContext, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import ApiProxy from "../../../app/api/admin/proxy/ApiProxy";
import ApiProxyGroup from "../../../app/api/admin/proxy-group/ApiProxyGroup";
import { ContextList } from "../../modules/list/listContext";
import { ContextContent } from "../../modules/content/contentContext";
import Button from "../../components/button";
import InputCheckbox from "../../components/inputs/checkbox";
import Select from "../../components/inputs/select";
import ModalYesNo from "../../components/modals/modal-yes-no";
import { isNarrowScreen } from "../../../inc/Responsive";

// Включение / выключение / удаление — короткие запросы, но и их не шлём сотней разом.
const UPDATE_CONCURRENCY = 5;

// Пакетные эндпоинты есть у проверки и замера (POST proxies/check, proxies/probe) и у переноса в
// группу (POST proxy-groups/{id}/proxies/attach). Остальное — запросом на каждый
// выбранный, не больше limit одновременно. isStopped() — прервать: новые не начинаются,
// начатые доживают.
async function runPool(ids, limit, worker, isStopped = () => false) {
    const queue = [...ids];
    const runNext = async () => {
        while (queue.length > 0 && !isStopped()) {
            await worker(queue.shift());
        }
    };
    await Promise.all(Array.from({ length: Math.min(limit, ids.length) }, runNext));
}

// Ошибки по каждому прокси тостами не показываем — на сотне выбранных это сотня тостов.
// Итог — одной строкой в панели.
function quietApi(api) {
    api.__showErrors = false;
    return api;
}

// Чекбокс «выбрать все» — только строки ТЕКУЩЕЙ страницы списка (contextList.list), как у устройств.
export function SelectAllCheckbox({ selectedIds, setSelectedIds, children }) {
    const contextList = useContext(ContextList);
    const pageIds = (contextList?.list ?? []).map((row) => row.id);
    const isAllSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

    const handleChange = (e) => {
        if (e.target.checked) {
            setSelectedIds((previous) => [...new Set([...previous, ...pageIds])]);
        } else {
            setSelectedIds((previous) => previous.filter((id) => !pageIds.includes(id)));
        }
    };

    return <InputCheckbox value={isAllSelected} onChange={handleChange}>{children}</InputCheckbox>;
}

// Панель массовых действий над выбранными прокси (липкая, внизу списка — как у устройств).
export default function ProxiesBatchBar({ selectedIds, onClear, onDeleted }) {
    const contextList = useContext(ContextList);
    const contextContent = useContext(ContextContent);
    const canEdit = contextContent.checkPermission('edit');
    const canDestroy = contextContent.checkPermission('destroy');

    // На телефоне панель стартует свёрнутой: нужен сначала список (тот же приём, что у устройств).
    const [_isOpen, set_isOpen] = useState(() => !isNarrowScreen());
    // Какое действие идёт: 'check' | 'probe' | 'enable' | 'disable' | 'destroy' | 'attach' | 'detach' | null.
    const [_action, set_action] = useState(null);
    const [_progress, set_progress] = useState(null); // {done, total}
    const [_summary, set_summary] = useState(null);   // строка итога последнего действия
    const [_isOpenModalDestroy, set_isOpenModalDestroy] = useState(false);
    const [_isOpenModalProbe, set_isOpenModalProbe] = useState(false);
    const [_groupId, set_groupId] = useState(''); // группа для «В группу»
    const proxyGroups = useSelector((state) => state.option.proxyGroups);
    const isStoppedRef = useRef(false);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            // Сняли выбор — панель размонтирована: не начинаем новых запросов впустую.
            isMountedRef.current = false;
            isStoppedRef.current = true;
        };
    }, []);

    // Общий каркас: прогресс, остановка, итог, перечитать список. worker(id) → ключ исхода.
    const runAction = async (action, limit, worker, formatSummary) => {
        if (_action !== null) return;
        const ids = [...selectedIds];
        const counts = {};
        let done = 0;
        isStoppedRef.current = false;
        set_action(action);
        set_summary(null);
        set_progress({ done: 0, total: ids.length });
        try {
            await runPool(ids, limit, async (id) => {
                const outcome = await worker(id);
                counts[outcome] = (counts[outcome] ?? 0) + 1;
                done += 1;
                if (isMountedRef.current) set_progress({ done, total: ids.length });
            }, () => isStoppedRef.current);
        } finally {
            const stoppedNote = done < ids.length ? ` Остановлено: не обработано ${ids.length - done}.` : '';
            const summary = formatSummary(counts) + stoppedNote;
            // Итог — ещё и тостом: удаление всех выбранных снимает выбор, и панель с итогом закрывается.
            new ApiProxy().addMessages(summary, counts.failed ? 'warning' : 'success');
            if (isMountedRef.current) {
                set_summary(summary);
                set_action(null);
                set_progress(null);
            }
            contextList.reload();
        }
    };

    // Проверка и замер — одним запросом каждый: бэкенд ставит выбранные в очередь и отвечает
    // сразу, работает крон. Ход виден в самом списке (статусы очереди, он сам перечитывается) —
    // здесь только сколько поставлено. В data — и уже стоявшие в очереди / шедшие: второй раз
    // бэкенд их не ставит.
    const runQueued = async (action, buildApi, label) => {
        if (_action !== null) return;
        set_action(action);
        set_summary(null);
        try {
            const data = await buildApi(selectedIds).getResponse();
            if (data) {
                const missingNote = data.missing.length > 0 ? `, удалены или не найдены: ${data.missing.length}` : '';
                set_summary(`${label}: ${data.data.length}${missingNote}. Статусы обновятся в списке.`);
            }
        } finally {
            if (isMountedRef.current) set_action(null);
            contextList.reload();
        }
    };

    const handleCheck = () => runQueued('check', (ids) => ApiProxy.CheckMany(ids), 'На проверке');
    const handleProbe = () => {
        set_isOpenModalProbe(false);
        runQueued('probe', (ids) => ApiProxy.ProbeMany(ids), 'На замере соединений');
    };

    const handleActive = (isActive) => runAction(isActive ? 'enable' : 'disable', UPDATE_CONCURRENCY, async (id) => {
        const data = await quietApi(ApiProxy.Update(id, { is_active: isActive })).getResponse();
        return data ? 'ok' : 'failed';
    }, (counts) => `${isActive ? 'Включено' : 'Выключено'}: ${counts.ok ?? 0}`
        + `${counts.failed ? `, с ошибкой ${counts.failed}` : ''}.`);

    const handleDestroy = async () => {
        set_isOpenModalDestroy(false);
        const deletedIds = [];
        await runAction('destroy', UPDATE_CONCURRENCY, async (id) => {
            const api = quietApi(ApiProxy.Destroy(id));
            await api.getResponse();
            const isDeleted = (api.code >= 200) && (api.code <= 299);
            if (isDeleted) deletedIds.push(id);
            return isDeleted ? 'ok' : 'failed';
        }, (counts) => `Удалено: ${counts.ok ?? 0}${counts.failed ? `, с ошибкой ${counts.failed}` : ''}.`);
        // Удалённых в списке больше нет — и в выборе держать их незачем.
        if (deletedIds.length > 0) onDeleted(deletedIds);
    };

    // «В группу» — одним запросом: attach ПЕРЕНОСИТ прокси из другой группы (группа у прокси одна).
    // Итог — сколько прокси в группе стало (affected фабрика ApiProxyGroup не отдаёт).
    const handleAttachGroup = async () => {
        if ((_action !== null) || (_groupId === '')) return;
        const groupLabel = proxyGroups[_groupId] ?? `#${_groupId}`;
        set_action('attach');
        set_summary(null);
        try {
            const data = await ApiProxyGroup.AttachProxies(Number(_groupId), selectedIds.map(Number)).getResponse();
            if (data) {
                const summary = `В группу «${groupLabel}»: ${selectedIds.length}. Всего в группе: ${data.proxies_count}.`;
                if (isMountedRef.current) set_summary(summary);
            }
        } finally {
            if (isMountedRef.current) set_action(null);
            contextList.reload();
        }
    };

    // «Убрать из группы» — из любой: proxies/detach снимает только из ОДНОЙ указанной группы, а
    // выбранные могут быть в разных. Поэтому PATCH proxy_group_id: null на каждый.
    const handleDetachGroup = async () => {
        await runAction('detach', UPDATE_CONCURRENCY, async (id) => {
            const data = await quietApi(ApiProxy.Update(id, { proxy_group_id: null })).getResponse();
            return data ? 'ok' : 'failed';
        }, (counts) => `Теперь без группы: ${counts.ok ?? 0}${counts.failed ? `, с ошибкой ${counts.failed}` : ''}.`);
    };

    const isBusy = _action !== null;
    const buttonLabel = (action, label) => ((_action === action && _progress)
        ? `${label}… ${_progress.done}/${_progress.total}`
        : label);

    return <div className="proxies__batch-bar">
        <div className="proxies__batch-header">
            <button type="button" className="proxies__batch-toggle" onClick={() => set_isOpen((isOpen) => !isOpen)}>
                {_isOpen ? '▾' : '▸'} Действия
            </button>
            <span className="proxies__batch-count">Выбрано: {selectedIds.length}</span>
            <button type="button" className="proxies__batch-clear" disabled={isBusy} onClick={onClear}>Снять выбор</button>
            {/* Только у действий запросом на каждый прокси — проверка идёт одним запросом. */}
            {_progress ? <Button onClick={() => { isStoppedRef.current = true; }}>Остановить</Button> : null}
        </div>

        {_summary ? <div className="proxies__batch-summary">{_summary}</div> : null}

        <div className={`proxies__batch-sections${_isOpen ? '' : ' proxies__batch-sections_hidden'}`}>
            {canEdit ? <div className="proxies__batch-section">
                <div className="proxies__batch-section-title">Проверка</div>
                <div className="proxies__batch-row">
                    <Button className="btn-success" disabled={isBusy} onClick={handleCheck}>
                        {_action === 'check' ? 'Ставлю в очередь…' : 'Проверить'}
                    </Button>
                </div>
                <div className="proxies__hint">
                    В очередь: крон проверяет пачками по 20, пачка — 15–80 с.
                </div>
            </div> : null}

            {canEdit ? <div className="proxies__batch-section">
                <div className="proxies__batch-section-title">Одновременные соединения</div>
                <div className="proxies__batch-row">
                    <Button className="btn-success" disabled={isBusy} onClick={() => set_isOpenModalProbe(true)}>
                        {_action === 'probe' ? 'Ставлю в очередь…' : 'Замерить'}
                    </Button>
                </div>
                <div className="proxies__hint">
                    В очередь: крон меряет пачками по 5, пачка — около минуты. Замер даёт сотни
                    соединений с адреса хаба — провайдер может перестать его пускать. Мерьте только
                    нужные, а не весь справочник.
                </div>
            </div> : null}

            {canEdit ? <div className="proxies__batch-section">
                <div className="proxies__batch-section-title">Управление</div>
                <div className="proxies__batch-row">
                    <Button disabled={isBusy} onClick={() => handleActive(true)}>
                        {buttonLabel('enable', 'Включить')}
                    </Button>
                    <Button className="btn-warn" disabled={isBusy} onClick={() => handleActive(false)}>
                        {buttonLabel('disable', 'Выключить')}
                    </Button>
                </div>
            </div> : null}

            {canEdit ? <div className="proxies__batch-section">
                <div className="proxies__batch-section-title">Группа</div>
                <div className="proxies__batch-row">
                    <Select className="proxies__batch-group-select" options={proxyGroups} value={_groupId}
                        hasLabel={false} isNull={true} readOnly={isBusy}
                        onChange={(e) => set_groupId(e.target.value)}/>
                    <Button className="btn-success" disabled={isBusy || (_groupId === '')} onClick={handleAttachGroup}>
                        {_action === 'attach' ? 'Переношу…' : 'В группу'}
                    </Button>
                </div>
                <div className="proxies__batch-row">
                    <Button disabled={isBusy} onClick={handleDetachGroup}>
                        {buttonLabel('detach', 'Убрать из группы')}
                    </Button>
                </div>
                <div className="proxies__hint">
                    Группа у прокси одна: прокси из другой группы в выбранную переносятся.
                    «Убрать из группы» снимает любую группу.
                </div>
            </div> : null}

            {canDestroy ? <div className="proxies__batch-section">
                <div className="proxies__batch-section-title">Удаление</div>
                <div className="proxies__batch-row">
                    <Button className="btn-danger" disabled={isBusy} onClick={() => set_isOpenModalDestroy(true)}>
                        {buttonLabel('destroy', 'Удалить')}
                    </Button>
                </div>
            </div> : null}
        </div>

        {/* Замер — не бесплатный: после пачки по 500 соединений провайдер уже отказывал хабу
            (connection refused у 17 прокси из 20) — поэтому с подтверждением. */}
        <ModalYesNo
            isOpenModal={_isOpenModalProbe}
            closeModal={() => set_isOpenModalProbe(false)}
            actionBtnYes={handleProbe}
            title={`Замерить соединения у ${selectedIds.length} прокси? Провайдер может перестать пускать адрес хаба.`}
        />

        <ModalYesNo
            isOpenModal={_isOpenModalDestroy}
            closeModal={() => set_isOpenModalDestroy(false)}
            actionBtnYes={handleDestroy}
            title={`Удалить выбранные прокси (${selectedIds.length})?`}
        />
    </div>;
}
